import React from 'react';

export const EVENTS = {
  SEARCH_INPUT_CHANGE: 'SEARCH_INPUT_CHANGE',
  FETCH_LIST_STARTED: 'FETCH_LIST_STARTED',
  FETCH_LIST_SUCCESS: 'FETCH_LIST_SUCCESS',
  FETCH_LIST_FAILED: 'FETCH_LIST_FAILED',
  FETCH_CONTENT_STARTED: 'FETCH_CONTENT_STARTED',
  FETCH_CONTENT_SUCCESS: 'FETCH_CONTENT_SUCCESS',
  FETCH_CONTENT_FAILED: 'FETCH_CONTENT_FAILED',
  SELECT_NEW_CATEGORY: 'SELECT_NEW_CATEGORY',
  SELECT_GLOSSARY_LIST_ITEM: 'SELECT_GLOSSARY_LIST_ITEM',
  SELECT_GLOSSARY_TOOLTIP_ITEM: 'SELECT_GLOSSARY_TOOLTIP_ITEM',
};

const INITIAL_STATE = {
  searchText: '',
  selected: '',
  categories: [],
  listApi: {loading: null, error: null},
  glossaryList: [],
  contentApi: {loading: null, error: null},
  glossaryItem: {},
};

function glossaryReducer(state, action) {
  switch (action.type) {
    case EVENTS.SEARCH_INPUT_CHANGE:
      return {...state, searchText: action.payload};
    case EVENTS.FETCH_LIST_STARTED:
      return {...state, listApi: {error: false, loading: true}};
    case EVENTS.FETCH_LIST_SUCCESS:
      return handleFetchListSuccess(state, action.payload);
    case EVENTS.FETCH_LIST_FAILED:
      return {...state, listApi: {loading: false, error: true}};
    case EVENTS.FETCH_CONTENT_STARTED:
      return {...state, contentApi: {...state.contentApi, loading: true}};
    case EVENTS.FETCH_CONTENT_SUCCESS:
      return {
        ...state,
        contentApi: {loading: false, error: false},
        glossaryItem: action.payload,
      };
    case EVENTS.FETCH_CONTENT_FAILED:
      return {...state, contentApi: {loading: false, error: true}};
    case EVENTS.SELECT_NEW_CATEGORY:
      return updateSelectedCategory(state, action.payload);
    case EVENTS.SELECT_GLOSSARY_LIST_ITEM:
      return updateGlossaryListItemSelection(state, action.payload);
    case EVENTS.SELECT_GLOSSARY_TOOLTIP_ITEM:
      return updateGlossaryTooltipItemSelection(state, action.payload);
    default:
      return {...state};
  }
}

function handleFetchListSuccess(state, payload) {
  let listApi = {loading: false, error: false};
  let glossaryList = payload;
  if (glossaryList.length) {
    glossaryList = updateListItemSelected(glossaryList, glossaryList[1].id);
  }
  return {...state, listApi, glossaryList};
}

function updateListItemSelected(glossaryList, selectedId) {
  return glossaryList.map((listItem) => {
    const newItem = {...listItem, selected: false};
    if (newItem.id === selectedId) {
      newItem.selected = true;
    }
    return newItem;
  });
}

function updateSelectedCategory(state, payload) {
  let categories = state.categories.map((listItem) => {
    let newItem = {...listItem, selected: false};
    if (newItem.value === payload) {
      newItem.selected = true;
    }
    return newItem;
  });
  return {...state, categories};
}

function updateGlossaryListItemSelection(state, payload) {
  let glossaryList = state.glossaryList.map((listItem) => {
    let newItem = {...listItem, selected: false};
    if (newItem.id === payload) {
      newItem.selected = true;
    }
    return newItem;
  });

  return {...state, glossaryList, contentApi: {...state.contentApi, loading: true}};
}

function updateGlossaryTooltipItemSelection(state, payload) {

  let glossaryList = state.glossaryList.map((listItem) => {
    let newItem = {...listItem, selected: false};
    if (newItem.id === payload.id) {
      newItem.selected = true;
    }
    return newItem;
  });

  let categories = state.categories.map((listItem) => {
    let newItem = {...listItem, selected: false};
    if (newItem.value === payload.category) {
      newItem.selected = true;
    }
    return newItem;
  });

  let searchText = payload.searchText;

  return {
    ...state,
    categories,
    searchText,
    glossaryList
  };
}

/**
 * useGlossaryHook :
 *  - The custom hook that holds the data for Glossary and Handles all its
 *    top level opeartions and logic.
 */
export function useGlossaryHook(props) {
  const [state, dispatch] = React.useReducer(glossaryReducer, {
    ...INITIAL_STATE,
    searchText: props.searchText,
    categories: props.categories,
  });

  /**
   * When You update Selected Categories or Update the list -> fetchGlossaryList
   */
  React.useEffect(() => {
    if (state.categories) {
      onFetchGlossary();
    }
  }, [state.categories, state.selected]);

  /**
   * When you update the glossary List; Likely a new item is selected
   * This makes us fetch the selected GlossaryItem Again.
   */
  React.useEffect(() => {
    if (state.glossaryList && state.glossaryList.length) {
      onFetchGlossaryItem();
    }
  }, [state.glossaryList]);

  /**
   * Gets id of currently selected Glossary List Item.
   */
  const getSelectedGlossaryItem = () => {
    let selectedGlossaryListItem = state.glossaryList.find(
      (listItem) => listItem.selected
    );
    return selectedGlossaryListItem.id;
  };

  /**
   * To get params for lising Glossary Items
   */
  const getFetchGlossaryParams = () => {
    let params = {};
    const selectedCategory = state.categories.find(
      (category) => category.selected
    );
    if (selectedCategory) {
      params.category = selectedCategory.value;
    }
    if (state.searchText) {
      params.search = state.searchText;
    }
    return params;
  };

  /**
   * To fetch the list of items to display on Glossary
   */
  async function onFetchGlossary() {
    try {
      dispatch({type: EVENTS.FETCH_LIST_STARTED});
      const glossaryList = await props.fetchGlossaryList(
        getFetchGlossaryParams()
      );
      dispatch({type: EVENTS.FETCH_LIST_SUCCESS, payload: glossaryList});
      if(state.selected){
        onSelectItem(state.selected)
      }
    } catch (error) {
      dispatch({type: EVENTS.FETCH_LIST_FAILED});
    }
  }

  /**
   * To fetch a single item's details for glossary
   */
  async function onFetchGlossaryItem() {
    try {
      dispatch({type: EVENTS.FETCH_CONTENT_STARTED});
      const glossaryItemContent = await props.fetchGlossaryItem(
        getSelectedGlossaryItem()
      );
      dispatch({
        type: EVENTS.FETCH_CONTENT_SUCCESS,
        payload: glossaryItemContent,
      });
    } catch (error) {
      dispatch({type: EVENTS.FETCH_CONTENT_FAILED});
    }
  }

  /**
   * Function to handle change in search Input.
   */
  function onSearchInputChange(event) {
    dispatch({type: EVENTS.SEARCH_INPUT_CHANGE, payload: event.target.value});
  }

  /**
   * Function to invoke when a new tab is selected.
   */
  function onTabSelect(newTabValue) {
    dispatch({type: EVENTS.SELECT_NEW_CATEGORY, payload: newTabValue});
  }

  /**
   * Function to invoke when you want to select Glossary Item
   */
  function onSelectItem(itemId) {
    dispatch({type: EVENTS.SELECT_GLOSSARY_LIST_ITEM, payload: itemId});
  }

  /**
   * Function to invoke when you want to select Glossary Item
   */
  function onSelectToolTip(payload) {
    dispatch({type: EVENTS.SELECT_GLOSSARY_TOOLTIP_ITEM, payload: payload});
  }

  return {
    state,
    onFetchGlossary,
    onFetchGlossaryItem,
    onSearchInputChange,
    onTabSelect,
    onSelectItem,
    onSelectToolTip
  };
}
